import { ClipboardList, Loader2, Plus } from 'lucide-react';
import type { PurchaseOrderSnippetItem } from '../../hooks/usePurchaseOrdersSnippetData';
import { formatCurrency } from '../../lib/utils';

interface InventoryPurchaseOrdersPanelProps {
  orders: PurchaseOrderSnippetItem[];
  pendingCount: number;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  onOrderClick?: (orderId: string) => void;
  onCreateOrder?: () => void;
}function formatOrderDate(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function statusLabel(status: PurchaseOrderSnippetItem['status']): string {
  if (status === 'pending') return 'Pending';
  if (status === 'received') return 'Received';
  return 'Cancelled';
}

function statusClass(status: PurchaseOrderSnippetItem['status']): string {
  if (status === 'pending') return 'bg-amber-100 text-amber-800';
  if (status === 'received') return 'bg-green-100 text-green-800';
  return 'bg-gray-100 text-gray-500';
}

/** Purchase orders sidebar for the control-centre inventory widget. */
export default function InventoryPurchaseOrdersPanel({
  orders,
  pendingCount,
  loading = false,
  error = null,
  onRetry,
  onOrderClick,
  onCreateOrder,
}: InventoryPurchaseOrdersPanelProps) {
  const panelFont =
    "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', system-ui, sans-serif";

  const scrollAreaClass =
    'flex-1 min-h-0 overflow-y-auto ios-momentum-scroll p-2 pt-1.5 sm:p-2.5 sm:pt-2 max-lg:max-h-[12.5rem] max-lg:flex-none lg:max-h-[calc(min(380px,48vh)-2.75rem)]';

  return (
    <aside
      className="relative z-0 flex h-full min-h-0 flex-col overflow-hidden"
      data-cc-inset
      data-inventory-purchase-orders
      aria-label="Purchase orders"
      style={{ fontFamily: panelFont }}
    >
      <header
        className="flex shrink-0 items-center gap-2 border-b border-black/[0.06] bg-white px-3 py-2 sm:px-3 sm:py-2.5"
        data-cc-inset-header
      >
        <ClipboardList className="h-4 w-4 shrink-0 text-blue-600" strokeWidth={2.25} />
        <h3
          className="text-[14px] font-semibold text-gray-900 sm:text-[14px]"
          style={{ letterSpacing: '-0.01em' }}
        >
          Purchase orders
        </h3>
        {!loading ? (
          <span
            className={`rounded-full px-1.5 py-0.5 text-[11px] font-semibold tabular-nums leading-none ${
              pendingCount > 0 ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-500'
            }`}
          >
            {pendingCount}
          </span>
        ) : null}
        <div className="ml-auto flex shrink-0 items-center gap-1">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" /> : null}
          {onCreateOrder ? (
            <button
              type="button"
              onClick={onCreateOrder}
              className="flex h-7 w-7 items-center justify-center rounded-full bg-blue-600 text-white shadow-sm transition-all hover:bg-blue-700 active:scale-[0.96] touch-manipulation sm:h-8 sm:w-8"
              aria-label="Create purchase order"
              title="Create purchase order"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <Plus className="h-4 w-4 sm:h-[18px] sm:w-[18px]" strokeWidth={2.5} />
            </button>
          ) : null}
        </div>
      </header>
      {loading ? (
        <div className="flex flex-1 items-center justify-center py-6">
          <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
        </div>
      ) : error ? (
        <div className="flex flex-1 flex-col items-center justify-center px-3 py-4 text-center">
          <p className="text-xs text-red-600 sm:text-sm">{error}</p>
          {onRetry ? (
            <button
              type="button"
              onClick={onRetry}
              className="mt-2 text-[11px] font-semibold text-blue-600 sm:text-xs"
            >
              Retry
            </button>
          ) : null}
        </div>
      ) : orders.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center px-3 py-5 text-center">
          <ClipboardList className="mb-2 h-8 w-8 text-gray-300" strokeWidth={1.5} />
          <p className="text-[13px] font-medium text-gray-700 sm:text-[14px]">No purchase orders yet</p>
          <p className="mt-0.5 text-[12px] text-gray-500 sm:text-[12px]">
            Pending POs will appear here
          </p>
        </div>
      ) : (
        <div className={scrollAreaClass}>
          <ul className="space-y-1.5">
            {orders.map((order) => (
              <li key={order.id}>
                <button
                  type="button"
                  onClick={() => onOrderClick?.(order.id)}
                  className="flex w-full flex-col gap-1 rounded-xl border border-gray-200/80 bg-white/70 px-2.5 py-2 text-left transition-all touch-manipulation hover:bg-white active:scale-[0.99] active:bg-gray-50 sm:px-3 sm:py-2.5"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <div className="flex items-start justify-between gap-2">
                    <p
                      className="min-w-0 flex-1 truncate text-[13px] font-semibold text-gray-900"
                      style={{ letterSpacing: '-0.005em' }}
                    >
                      {order.supplierName}
                    </p>
                    <span
                      className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold leading-none ${statusClass(order.status)}`}
                    >
                      {statusLabel(order.status)}
                    </span>
                  </div>
                  <p className="truncate text-[11px] text-gray-500 tabular-nums">
                    {order.orderNumber}
                    {order.itemCount > 0
                      ? ` · ${order.receivedCount}/${order.itemCount} received`
                      : ''}
                  </p>
                  <div className="flex items-center justify-between gap-2 text-[12px] text-gray-500">
                    <span className="tabular-nums">{formatOrderDate(order.createdAt)}</span>
                    <span
                      className="font-semibold tabular-nums text-gray-800 text-[13px]"
                      style={{ letterSpacing: '-0.01em' }}
                    >
                      {formatCurrency(order.totalAmount)}
                    </span>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </aside>
  );
}
